import React from "react";
import { truncateText } from "../../../../../shared/utils/formatters";

const CIRCLE_R = 26;
const NAME_MAX_LENGTH = 24;
const CARD_FIXED_WIDTH_PX = 320;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_R;
const STROKE_WIDTH = 6;
const SIZE = 64;

export interface BankAccountCardProps {
  name: string;
  balance: string;
  percent: number;
  trend?: "up" | "down" | "neutral";
  trendLabel?: string;
  isConnected: boolean;
  /** Optional accent color for the ring (e.g. orange, blue). Default neutral for 0%. */
  ringColor?: "orange" | "blue" | "neutral";
  /** Total pending transactions (uncategorized, not deleted). */
  pendingTransactionsCount?: number;
}

const RING_COLORS: Record<
  NonNullable<BankAccountCardProps["ringColor"]>,
  string
> = {
  orange: "#F97316",
  blue: "#3B82F6",
  neutral: "#e5e5e5",
};

export function BankAccountCard({
  name,
  balance,
  percent,
  trend = "neutral",
  trendLabel,
  isConnected,
  ringColor = percent > 0 ? "orange" : "neutral",
  pendingTransactionsCount = 0,
}: BankAccountCardProps) {
  const clampedPercent = Math.min(100, Math.max(0, percent));
  const strokeDashoffset =
    CIRCLE_CIRCUMFERENCE - (clampedPercent / 100) * CIRCLE_CIRCUMFERENCE;
  const strokeColor = RING_COLORS[ringColor];

  // const trendEl =
  //   trend === "up" ? (
  //     <span className="text-caption-bold font-caption-bold text-success-500">
  //       ▲ {trendLabel ?? ""}
  //     </span>
  //   ) : trend === "down" ? (
  //     <span className="text-caption-bold font-caption-bold text-error-500">
  //       ▼ {trendLabel ?? ""}
  //     </span>
  //   ) : (
  //     <span className="text-caption font-caption text-subtext-color">
  //       {trendLabel ?? "— no change"}
  //     </span>
  //   );

  return (
    <div
      className="flex shrink-0 items-center gap-4 rounded-lg bg-default-background px-6 py-5 shadow-sm"
      style={{ width: CARD_FIXED_WIDTH_PX, minWidth: CARD_FIXED_WIDTH_PX }}
    >
      <div className="relative flex h-16 w-16 flex-none items-center justify-center">
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="absolute inset-0 -rotate-90"
          aria-hidden
        >
          {/* Background ring */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={CIRCLE_R}
            fill="none"
            stroke="#e5e5e5"
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress ring */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={CIRCLE_R}
            fill="none"
            stroke={strokeColor}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCLE_CIRCUMFERENCE}
            strokeDashoffset={strokeDashoffset}
            className="transition-[stroke-dashoffset] duration-300 ease-out"
          />
        </svg>
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-default-background relative z-10 px-0.5">
          <span className="text-center text-[10px] font-bold tabular-nums leading-none text-default-font">
            {clampedPercent.toFixed(2)}%
          </span>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 overflow-hidden">
        <div className="flex w-full min-w-0 items-center gap-2">
          <span
            className="min-w-0 truncate text-caption font-caption text-subtext-color"
            title={name}
          >
            {truncateText(name, NAME_MAX_LENGTH)}
          </span>
          {isConnected && (
            <div className="flex shrink-0 items-center gap-1 rounded-full bg-success-50 px-2 py-0.5">
              <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-success-600" />
              <span className="font-['Inter'] text-[10px] font-[700] leading-[15px] text-success-600">
                Connected
              </span>
            </div>
          )}
        </div>
        <span className="text-heading-3 font-heading-3 text-default-font">
          {balance}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          Pending {pendingTransactionsCount} transactions
        </span>
        {/* {trendEl} */}
      </div>
    </div>
  );
}
